import { useEffect, useState } from "react";
import { useEventBus } from "src/hooks";
import { usePreviousValue } from "src/hooks/Data";
import {
  ON_CHANGE_EVOLUTIVE_CROSS_BUTTON_STATE,
  ON_RENDER_EVOLUTIVE_CROSS_FOOTER_OPTIONS,
  ON_UPDATE_STEP_ONE_DATA,
  ON_UPDATE_STEP_THREE_DATA,
  ON_UPDATE_STEP_TWO_DATA,
} from "src/hooks/EventBus/eventTypes";

// Constantes identificadoras de cada step.
const STEP_ONE = 0;
const STEP_TWO = 1;
const STEP_THREE = 2;

// event bus
const eventBus = useEventBus();

/**
 * @param {number} step Identificador do step
 * @returns {boolean} true se o step é valido, false caso contrário.
 */
const isStepDone = function(step) {
  const validations = {
    [STEP_ONE]: {
      isAllDone() {
        return !!StepManager.step_one_data.length;
      },
    },
    [STEP_TWO]: {
      isAllDone() {
        return StepManager.step_two_data.length > 1;
      },
    },
    [STEP_THREE]: {
      isAllDone() {
        return !!StepManager.step_three_data.length;
      },
    },
  };

  return validations[step].isAllDone();
};

/**
 * @param {number} step Identificador do step
 * @returns Nome do evento de input de dados iniciais correspondentes para o step ativo.
 */
const getEventNameByStep = function(step) {
  return [
    ON_UPDATE_STEP_ONE_DATA,
    ON_UPDATE_STEP_TWO_DATA,
    ON_UPDATE_STEP_THREE_DATA,
  ][step];
};

/**
 * Gerenciador de steps do evolutivo de cruzamentos, são suas responsabilidades:
 * - Realizar a mudança de estado do step visível (onNext e onGoTo)
 * - Notificar para os steps seus respectivos dados de entrada (Por exemplo, os dados de entrada so step 1 são os dados
 * de saída do step 1.)
 */
export class StepManager {
  constructor() {
    // States para lidar com visibilidade
    const [stepActive, setStepActive] = useState(STEP_ONE);
    // Controle de quais steps são clicáveis (header)
    const [joinedSteps, setJoinedSteps] = useState([0]);
    // Capta o valor anterior do passo visível
    const previousStepValue = usePreviousValue(stepActive);

    // Footer options
    const [footerOptions, setFooterOptions] = useState({
      visible: false,
      count: 0,
      type: StepManager.constants.QUESTION_TYPE,
    });

    // Externaliza os estados
    this.stepActive = stepActive;
    this.joinedSteps = joinedSteps;
    this.previousStepValue = previousStepValue;
    this.footerOptions = footerOptions;

    // Setters
    this.setJoinedSteps = setJoinedSteps;
    this.setStepActive = setStepActive;
    this.setFooterOptions = setFooterOptions;

    // Eventos internos
    this._onChangeFooterOptions();
  }

  // constantes
  static constants = {
    QUESTION_TYPE: "questions",
    ALTERNATIVE_TYPE: "alternatives",
    STEP_ONE: STEP_ONE,
    STEP_TWO: STEP_TWO,
    STEP_THREE: STEP_THREE,
  };

  // Dados de saída do step um.
  static step_one_data = [];
  // Dados de saída do step dois.
  static step_two_data = [];
  // Dados de saída do step três.
  static step_three_data = [];

  /**
   * Atualiza internamente os dados de saída dado um step.
   * @param {number} step Identificador do step
   * @param {array} data Dados de saída do step correspondente.
   */

  static updateStepData(step, data) {
    switch (step) {
      case STEP_ONE:
        StepManager.step_one_data = data;
        return;
      case STEP_TWO:
        StepManager.step_two_data = data;
        return;
      case STEP_THREE:
        StepManager.step_three_data = data;
        StepManager.changeNextButtonVisibility(!!data.length);
        return;
      default:
        return null;
    }
  }

  /**
   * Recupera os dados de saída correspondentes ao step de parâmetro.
   * @param {number} step Identificador do step
   * @returns {array} Dados de saída correspondente ao step passado como parâmetro.
   */
  static getDataByStep(step) {
    switch (step) {
      case STEP_ONE:
        return StepManager.step_one_data;
      case STEP_TWO:
        return StepManager.step_two_data;
      case STEP_THREE:
        return StepManager.step_three_data;
      default:
        return [];
    }
  }

  /**
   * Reseta os dados de saída de todos os steps.
   */
  static resetData() {
    StepManager.step_one_data = [];
    StepManager.step_two_data = [];
    StepManager.step_three_data = [];
  }

  static changeNextButtonVisibility(visible) {
    eventBus.notify(ON_CHANGE_EVOLUTIVE_CROSS_BUTTON_STATE, visible);
  }

  /**
   * PRIVATE: Seta os estados dos steps que já foram visitados. É utilizado para controle de fluxo.
   * @param {number} step Identificador do step
   */
  _pushStep(step) {
    if (!this.joinedSteps.includes(step)) {
      this.setJoinedSteps([...this.joinedSteps, step]);
    }
  }

  /**
   * PRIVATE: Envia uma notificação para um step, essa notificação passará os dados de entrada para o step.
   * @param {number} step Identificador do step
   */
  _outputDataByStep(step) {
    eventBus.notify(
      getEventNameByStep(step),
      StepManager.getDataByStep(step - 1)
    );
  }

  /**
   * Seta o próximo estado visível
   */
  onNext() {
    const nextStep = this.stepActive + 1;
    this._pushStep(this.stepActive);
    this.setStepActive(nextStep);

    this._outputDataByStep(nextStep);
    this._handleNextButtonDisplay(nextStep, false);

    this._handleFooterOptionsDisplay(nextStep, this.stepActive);
  }

  /**
   * Seta o estado visivel do passo anterior
   */
  onBack() {
    const beforeStep = this.stepActive - 1;
    this._pushStep(this.stepActive);
    this.setStepActive(beforeStep);
  }

  /**
   * PRIVATE: Habilita ou desabilita o botão "avançar" de acordo com o passo atual em que o usuário se encontra.
   * @param {number} step Identificador do step
   * @param {boolean} returning Indica se o usuário está voltando para um step anterior
   */
  _handleNextButtonDisplay(step, returning = false) {
    let visible = returning || isStepDone(step);

    StepManager.changeNextButtonVisibility(visible);
  }

  /**
   * PRIVATE: Seta o estado de visibilidade de footer options ao trocar de step.
   */
  _handleFooterOptionsDisplay() {
    eventBus.notify(ON_RENDER_EVOLUTIVE_CROSS_FOOTER_OPTIONS, {
      ...this.footerOptions,
      visible: false,
    });
  }

  /**
   * Pula para o step de parâmetro.
   * @param {number} step Identificador do step
   */
  onGoTo(step) {
    const prevStep = this.stepActive;
    const nextStep = step;

    if (nextStep === prevStep) return;

    // Se o step clicado é inferior ao step atual, o usuário irá para o step de target.
    if (nextStep < prevStep) {
      this._pushStep(prevStep);
      this.setStepActive(nextStep);
      this._handleNextButtonDisplay(nextStep, true);
      this._handleFooterOptionsDisplay(nextStep, prevStep);
    }
    // Se o step está concluido, o step clicado é superior ao step atual, e o step clicado é diferente do step anterior
    else if (
      (isStepDone(prevStep) || nextStep === STEP_THREE) &&
      this.joinedSteps.includes(nextStep)
    ) {
      this.setStepActive(nextStep);
      this._handleNextButtonDisplay(nextStep, false);
      this._handleFooterOptionsDisplay(nextStep, prevStep);
    }
  }

  /**
   * EFFECT PRIVADO: Controla o estado de visibilidade do footer options.
   */
  _onChangeFooterOptions() {
    useEffect(() => {
      eventBus.subscribe(
        ON_RENDER_EVOLUTIVE_CROSS_FOOTER_OPTIONS,
        ({ count, visible, type }) => {
          this.setFooterOptions({
            count,
            visible: visible,
            type,
          });
        }
      );
    }, [this.footerOptions]);
  }

  // Visibilidade de steps
  stepOneIsVisible() {
    return this.stepActive === 0;
  }

  stepTwoIsVisible() {
    return this.stepActive === 1;
  }

  stepThreeIsVisible() {
    return this.stepActive === 2;
  }
}
